import React from 'react';
import CitedMarkdown from '../../../../../components/markdown/CitedMarkdown';
import { label as tLabel } from '../../../report/utils/reportLabelTranslations';
import {
  IN_DEPTH_SECTION_LABELS,
  parseInDepthMarkdown,
  sectionsHaveContent,
} from '../../../report/utils/inDepthAnalysisSections';
import { joinNames } from '../../../../../utils/format';

const TONES = {
  green: 'bg-green-50 border-green-500',
  blue: 'bg-blue-50 border-blue-500',
  purple: 'bg-purple-50 border-purple-500',
  red: 'bg-red-50 border-red-500',
  orange: 'bg-orange-50 border-orange-500',
  amber: 'bg-amber-50 border-amber-500',
  indigo: 'bg-indigo-50 border-indigo-500',
  yellow: 'bg-yellow-50 border-yellow-500',
};

const hasEntries = (value) => value && typeof value === 'object' && Object.keys(value).length > 0;

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h4 className="text-lg font-semibold text-gray-900 mb-4">{title}</h4>
    {children}
  </div>
);

const Group = ({ title, children }) => (
  <div className="mb-6">
    <h5 className="text-sm font-semibold text-gray-700 mb-3">{title}</h5>
    <div className="space-y-3">{children}</div>
  </div>
);

/**
 * One agreement/difference/consensus point. `citedTitle` renders the key with
 * citation links (used where the model writes the whole point into the key).
 */
function Point({ title, point, tone, citedTitle, citations }) {
  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${TONES[tone]}`}>
      {citedTitle
        ? <CitedMarkdown text={title} maxLength={500} className="font-semibold text-gray-900 mb-2" {...citations} />
        : <h6 className="font-semibold text-gray-900 mb-2">{title}</h6>}
      {point.Speakers && (
        <p className="text-xs text-gray-600 mb-2"><span className="font-medium">Speakers:</span> {joinNames(point.Speakers)}</p>
      )}
      {point.Arguments && <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{point.Arguments}</div>}
      {point.Summary && (
        <CitedMarkdown text={point.Summary} maxLength={500} className="text-sm text-gray-800 font-medium mt-2" {...citations} />
      )}
      {point.Explanation && (
        <CitedMarkdown text={point.Explanation} maxLength={500} className="text-sm text-gray-800 font-medium mt-2 italic" {...citations} />
      )}
      {point.Policy_context && (
        <div className="mt-3 bg-white/60 border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-gray-700 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
          <CitedMarkdown
            text={point.Policy_context}
            maxLength={300}
            className="text-sm text-gray-800 leading-relaxed"
            sourceReferences={citations.sourceReferences}
            onCitationClick={citations.onCitationClick}
          />
        </div>
      )}
    </div>
  );
}

const PointGroup = ({ title, points, tone, citedTitle, citations }) => (hasEntries(points) ? (
  <Group title={title}>
    {Object.entries(points).map(([key, point]) => (
      <Point key={key} title={key} point={point} tone={tone} citedTitle={citedTitle} citations={citations} />
    ))}
  </Group>
) : null);

const Assessment = ({ title, summary, level }) => (summary || level ? (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <h5 className="text-sm font-semibold text-gray-700 mb-2">{title}</h5>
    {summary && <p className="text-sm text-gray-700 mb-2">{summary}</p>}
    {level && <p className="text-xs text-gray-600 italic">{level}</p>}
  </div>
) : null);

const TAKEAWAYS = [
  ['Key takeaways', 'Key Takeaways', 'blue'],
  ['Resolutions and action items', 'Resolutions and action items', 'green'],
  ['Unresolved issues', 'Unresolved issues', 'yellow'],
  ['Suggested compromises', 'Suggested compromises', 'purple'],
];

function StructuredAnalysis({ analysis, lang, citations }) {
  const t = (text) => tLabel(text, lang);
  const agreements = analysis.Agreements;
  const differences = analysis.Differences;
  const partial = analysis['Partial agreements']?.['Partial agreements'];
  const takeaways = analysis.Takeaways;
  const comments = analysis['Thought provoking comments'];
  const questions = analysis['Follow-up questions'];
  const groupProps = { citations };

  return (
    <div className="space-y-6">
      {agreements && (
        <Card title={t('Agreements')}>
          <PointGroup title={t('Agreement points')} points={agreements['Agreement points']} tone="green" {...groupProps} />
          <PointGroup title={t('Similar viewpoints')} points={agreements['Similar viewpoints']} tone="blue" citedTitle {...groupProps} />
          <PointGroup title={t('Unexpected consensus')} points={agreements['Unexpected consensus']} tone="purple" {...groupProps} />
          <Assessment
            title={t('Overall assessment')}
            summary={agreements['Overall assessment']?.Summary}
            level={agreements['Overall assessment']?.['Consensus level']}
          />
        </Card>
      )}

      {differences && (
        <Card title={t('Differences')}>
          <PointGroup title={t('Different viewpoints')} points={differences['Different viewpoints']} tone="red" {...groupProps} />
          <PointGroup title={t('Unexpected differences')} points={differences['Unexpected differences']} tone="orange" {...groupProps} />
          <Assessment
            title={t('Overall assessment')}
            summary={differences['Overall assessment']?.Summary}
            level={differences['Overall assessment']?.['Disagreement level']}
          />
        </Card>
      )}

      {hasEntries(partial) && (
        <Card title={t('Partial Agreements')}>
          <div className="space-y-3">
            {Object.entries(partial).map(([key, point]) => (
              <Point key={key} title={key} point={point} tone="amber" citedTitle citations={citations} />
            ))}
          </div>
        </Card>
      )}

      {takeaways && (
        <Card title={t('Key Takeaways')}>
          {TAKEAWAYS.filter(([field]) => takeaways[field]).map(([field, title, tone]) => (
            <div key={field} className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 mb-2">{t(title)}</h5>
              <div className={`text-sm text-gray-700 whitespace-pre-line border-l-4 rounded-r-lg p-4 ${TONES[tone]}`}>{takeaways[field]}</div>
            </div>
          ))}
        </Card>
      )}

      {comments && (
        <Card title={t('Thought-Provoking Comments')}>
          <div className="space-y-4">
            {Object.entries(comments).filter(([key]) => key !== 'Overall assessment').map(([quote, comment]) => (
              <div key={quote} className={`border-l-4 rounded-r-lg p-4 ${TONES.indigo}`}>
                <div className="text-sm font-semibold text-gray-900 mb-2 italic">"{quote}"</div>
                {comment.Speaker && <p className="text-xs text-gray-600 mb-2"><span className="font-medium">Speaker:</span> {comment.Speaker}</p>}
                {comment.Reason && <div className="text-sm text-gray-700 mb-2"><span className="font-medium">Reason:</span> {comment.Reason}</div>}
                {comment.Impact && <div className="text-sm text-gray-700"><span className="font-medium">Impact:</span> {comment.Impact}</div>}
              </div>
            ))}
            <Assessment title={t('Overall assessment')} summary={comments['Overall assessment']} />
          </div>
        </Card>
      )}

      {questions && (
        <Card title={t('Follow-up Questions')}>
          <div className="space-y-3">
            {Object.entries(questions).map(([question, detail]) => (
              <div key={question} className={`border-l-4 rounded-r-lg p-4 ${TONES.amber}`}>
                <div className="text-sm font-semibold text-gray-900 mb-2">{question}</div>
                {detail.Speaker && <p className="text-xs text-gray-600 mb-2"><span className="font-medium">Speaker:</span> {detail.Speaker}</p>}
                {detail.Explanation && <div className="text-sm text-gray-700">{detail.Explanation}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

const parseObject = (text) => {
  try {
    const value = JSON.parse(text);
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

/**
 * What to render. The session field `in_depth_md` (editor saves and translations)
 * wins over the generated file and may hold a JSON dict, flat section markdown
 * or, for translations, plain text.
 */
function resolveAnalysis(inDepthMd, fileAnalysis, isTranslated) {
  const md = inDepthMd?.trim();
  if (md) {
    if (isTranslated) {
      const dict = parseObject(md);
      return dict ? { kind: 'structured', analysis: dict } : { kind: 'text', text: inDepthMd };
    }
    if (md.startsWith('{')) {
      const dict = parseObject(md);
      if (dict) return { kind: 'structured', analysis: dict };
    } else {
      const sections = parseInDepthMarkdown(inDepthMd);
      if (sectionsHaveContent(sections)) return { kind: 'sections', sections };
    }
  }
  return hasEntries(fileAnalysis) ? { kind: 'structured', analysis: fileAnalysis } : null;
}

const analysisOf = (report) => resolveAnalysis(report.markdown.inDepth, report.data?.['Additional analysis'], report.isTranslated);

export const hasInDepthAnalysis = (report) => analysisOf(report) !== null;

export default function InDepthTab({ report, onCitationClick }) {
  const { lang, enrichment } = report;
  const citations = {
    transcriptReferences: enrichment.transcriptReferences,
    sourceReferences: enrichment.sourceReferences,
    onCitationClick,
  };
  const view = analysisOf(report);

  if (!view) return null;

  if (view.kind === 'text') {
    return (
      <Card title={tLabel('In-Depth Analysis', lang)}>
        <CitedMarkdown text={view.text} maxLength={1500} className="whitespace-pre-line" />
      </Card>
    );
  }

  if (view.kind === 'sections') {
    return (
      <div className="space-y-6">
        {IN_DEPTH_SECTION_LABELS.filter(({ key }) => view.sections[key]?.trim()).map(({ key, label }) => (
          <Card key={key} title={tLabel(label, lang)}>
            <CitedMarkdown text={view.sections[key].trim()} maxLength={1500} className="whitespace-pre-line" {...citations} />
          </Card>
        ))}
      </div>
    );
  }

  return <StructuredAnalysis analysis={view.analysis} lang={lang} citations={citations} />;
}
